import { upsertIdeas } from 'common/actions/ideas';
import queryActionFactory from 'common/actions/queryActionFactory';
import { DefaultIdeaSort } from 'common/constants/ideas';
import DataV2 from 'common/DataV2';
import { sortIdeas } from 'common/util/sortIdeas';

import type { Idea, IdeaIDOnly } from 'common/api/endpoints/idea';
import type { View } from 'common/api/endpoints/view';
import type { FiltersOperator } from 'common/fieldFilters/utils';
import type { Filter } from 'common/views/filters';
import type { Dispatch, GetState } from 'redux-connect';

export type QueryParams = {
  groupBy?: View['groupBy'];
  groupURLName?: string;
  parentID?: string;
  search?: string;
  sort?: View['sort'];
  viewURLName?: string;
  filtering?: {
    filters: Filter[];
    filtersOperator: FiltersOperator;
  };
};

export const {
  // query helpers
  getQueryKey,

  // action creators
  invalidateQueries: invalidateIdeaQueries,
  loadQuery,
  loadMore,
  reloadQuery: reloadIdeaQuery,

  // action types
  BulkRemoveType,
  BulkLoadedType,
  RequestType,
  RequestMoreType,
  LoadedType,
  LoadedMoreType,
  ErrorType,
  InvalidateType,
} = queryActionFactory<Idea, QueryParams>('ideaQueries', DataV2.queries.ideas, {
  // when new ideas are loaded, add the full idea objects to the `ideas` store
  onItemsLoaded: async (dispatch: Dispatch, items: Idea[]) => {
    await dispatch(upsertIdeas(items));
  },
});

export type AddIdeaToQueryAction = {
  type: typeof AddItemType;
  queryKey: string;
  item: Idea;
  sortedIdeaIDs: string[];
  timestamp: number;
};

export const AddItemType = 'canny/ideaQueries/addItem';

/**
 * Custom action creator for adding an item to a specific ideaQuery.
 * This action sorts the ideas based on the view's sort and groupBy configuration
 * to ensure the new idea appears in the correct position.
 */
export const addIdeaToQuery = (queryParams: QueryParams, item: Idea) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const queryKey = getQueryKey(queryParams);
    const state = getState();

    const existingQuery = state.ideaQueries?.[queryKey];
    if (!existingQuery) {
      return dispatch({
        type: AddItemType,
        queryKey,
        item,
        sortedIdeaIDs: [item._id],
        timestamp: Date.now(),
      });
    }

    const existingItems = existingQuery.items || [];
    const itemExists = existingItems.some(
      (existingItem: IdeaIDOnly) => existingItem._id === item._id,
    );

    if (itemExists) {
      return;
    }

    const fullIdeas: Idea[] = existingItems
      .map((ideaIDOnly: IdeaIDOnly) => {
        const ideaQueryKey = JSON.stringify({ ideaID: ideaIDOnly._id });
        const ideaQuery = state.ideas?.[ideaQueryKey];
        return ideaQuery?.items?.[0];
      })
      .filter((idea: Idea | undefined): idea is Idea => idea !== undefined);

    const view: Pick<View, 'sort' | 'groupBy'> = {
      sort: queryParams.sort ?? DefaultIdeaSort,
      groupBy: queryParams.groupBy ?? null,
    };

    const sortedIdeas = sortIdeas([...fullIdeas, item], view);
    const sortedIdeaIDs = sortedIdeas.map((idea) => idea._id);

    return dispatch({
      type: AddItemType,
      queryKey,
      item,
      sortedIdeaIDs,
      timestamp: Date.now(),
    });
  };
};
